import type {
  AdminSection,
  AdminCommentsListResponse,
  AdminCommentUserSearchResponse,
  CommentUserType,
  AnalystReportsResponse,
  FirmSuggestionsResponse,
  ApiResponse,
  Article,
  ArticlesListResponse,
  AssetTranslationAggregate,
  ChangePasswordRequest,
  CreateUserRequest,
  CreateUserResponse,
  ListUsersResponse,
  LoginRequest,
  LoginResponse,
  MeResponse,
  ResetPasswordResponse,
  SetCommentUserBlockedResponse,
  SetCommentUserProfileResponse,
  SetCommentUserTypeResponse,
  TranslationsListResponse,
  UpdateArticle,
} from "../../../shared/index";

const TOKEN_KEY = "tr_admin_token";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(
  path: string,
  init: RequestInit = {}
): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const token = getToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const response = await fetch(`/api${path}`, { ...init, headers });

  let payload: ApiResponse<T>;
  try {
    payload = await response.json();
  } catch {
    throw new ApiError(response.status, response.statusText || "Network error");
  }

  if (!response.ok || payload.error) {
    throw new ApiError(response.status, payload.error || response.statusText);
  }
  return payload.data as T;
}

export const api = {
  login: (body: LoginRequest) =>
    request<LoginResponse>("/auth/login", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  me: () => request<MeResponse>("/auth/me"),
  logout: () => request<{ success: true }>("/auth/logout", { method: "POST" }),
  changePassword: (body: ChangePasswordRequest) =>
    request<LoginResponse>("/auth/change-password", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  adminUsers: {
    list: () => request<ListUsersResponse>("/admin/users"),
    create: (body: CreateUserRequest) =>
      request<CreateUserResponse>("/admin/users", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    delete: (id: string) =>
      request<{ success: true }>(`/admin/users/${id}`, { method: "DELETE" }),
    setPermission: (id: string, section: AdminSection, enabled: boolean) =>
      request<{ user: import("../../../shared/index").AdminUser }>(
        `/admin/users/${id}/permissions`,
        {
          method: "PATCH",
          body: JSON.stringify({ section, enabled }),
        }
      ),
    setStatus: (id: string, isActive: boolean) =>
      request<{ user: import("../../../shared/index").AdminUser }>(
        `/admin/users/${id}/status`,
        {
          method: "PATCH",
          body: JSON.stringify({ isActive }),
        }
      ),
    resetPassword: (id: string) =>
      request<ResetPasswordResponse>(`/admin/users/${id}/reset-password`, {
        method: "POST",
      }),
  },

  ilWebsite: {
    listArticles: (params: { page: number; search?: string }) => {
      const qs = new URLSearchParams({ page: String(params.page) });
      if (params.search) qs.set("search", params.search);
      return request<ArticlesListResponse>(`/il-website/articles?${qs}`);
    },
    getArticle: (id: string) =>
      request<Article>(`/il-website/articles/${encodeURIComponent(id)}`),
    updateArticle: (id: string, body: UpdateArticle) =>
      request<Article>(`/il-website/articles/${encodeURIComponent(id)}`, {
        method: "PUT",
        body: JSON.stringify(body),
      }),
    deleteArticle: (id: string) =>
      request<{ success: true; deleted: string }>(
        `/il-website/articles/${encodeURIComponent(id)}`,
        { method: "DELETE" }
      ),
  },

  analystReports: {
    list: (params: {
      ticker?: string;
      firm?: string;
      firmId?: number;
      limit?: number;
    }) => {
      const qs = new URLSearchParams();
      if (params.ticker) qs.set("ticker", params.ticker);
      if (params.firmId !== undefined) qs.set("firmId", String(params.firmId));
      else if (params.firm) qs.set("firm", params.firm);
      if (params.limit) qs.set("limit", String(params.limit));
      const suffix = qs.toString();
      return request<AnalystReportsResponse>(
        `/analyst-reports${suffix ? `?${suffix}` : ""}`
      );
    },
    firms: (q?: string) => {
      const suffix = q ? `?q=${encodeURIComponent(q)}` : "";
      return request<FirmSuggestionsResponse>(`/analyst-reports/firms${suffix}`);
    },
  },

  translations: {
    list: (params: { page: number; limit: number; search?: string }) => {
      const qs = new URLSearchParams({
        page: String(params.page),
        limit: String(params.limit),
      });
      if (params.search) qs.set("search", params.search);
      return request<TranslationsListResponse>(`/translations?${qs}`);
    },
    get: (ticker: string) =>
      request<AssetTranslationAggregate>(
        `/translations/${encodeURIComponent(ticker)}`
      ),
    create: (body: { stockId: number; companyName: string }) =>
      request<{ stockId: number; companyName: string }>(`/translations`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    update: (ticker: string, body: AssetTranslationAggregate) =>
      request<{ success: true; stockId: number }>(
        `/translations/${encodeURIComponent(ticker)}`,
        {
          method: "PUT",
          body: JSON.stringify(body),
        }
      ),
  },

  comments: {
    list: (params: {
      page: number;
      limit: number;
      postSlug?: string;
      userId?: string;
      authorSlug?: string;
      deleted?: boolean;
      reported?: boolean;
    }) => {
      const qs = new URLSearchParams({
        page: String(params.page),
        limit: String(params.limit),
      });
      if (params.postSlug) qs.set("postSlug", params.postSlug);
      if (params.userId) qs.set("userId", params.userId);
      if (params.authorSlug) qs.set("authorSlug", params.authorSlug);
      if (params.deleted) qs.set("deleted", "true");
      if (params.reported) qs.set("reported", "true");
      return request<AdminCommentsListResponse>(`/comments?${qs}`);
    },
    searchUsers: (q: string) =>
      request<AdminCommentUserSearchResponse>(
        `/comments/search-users?q=${encodeURIComponent(q)}`
      ),
    delete: (id: string) =>
      request<{ success: true }>(`/comments/${encodeURIComponent(id)}`, {
        method: "DELETE",
      }),
    restore: (id: string) =>
      request<{ success: true }>(
        `/comments/${encodeURIComponent(id)}/restore`,
        { method: "PATCH" }
      ),
    setUserType: (userId: string, userType: CommentUserType, staticUserId?: string) =>
      request<SetCommentUserTypeResponse>(
        `/comments/set-user-type/${encodeURIComponent(userId)}`,
        { method: "POST", body: JSON.stringify({ userType, staticUserId }) }
      ),
    setUserBlocked: (staticUserId: string, isBlocked: boolean) =>
      request<SetCommentUserBlockedResponse>(
        `/comments/set-blocked/${encodeURIComponent(staticUserId)}`,
        { method: "POST", body: JSON.stringify({ isBlocked }) }
      ),
    setUserProfile: (
      staticUserId: string,
      fields: { displayName?: string; bio?: string }
    ) =>
      request<SetCommentUserProfileResponse>(
        `/comments/set-profile/${encodeURIComponent(staticUserId)}`,
        { method: "POST", body: JSON.stringify(fields) }
      ),
  },
};
